/**
 * The engine's dirty DAG (W1-05b engine-graph; ARCHITECTURE.md §7.1, DESIGN.md §6.2).
 *
 * `EngineGraph` keeps everything the engine compiles from a document source as nodes with an explicit stale
 * state, and recompiles only what the ChangeSets since the last `sync()` touched:
 *
 *   doc.pkg (+ EngineOptions.lower.overrides) -> lowering options, speed settings, effective pkg
 *   chart-level IR (META, BPMList, judgeLineGroup, timeTags, root keys)   -> tempo map (Phira op order)
 *   per line: IrLine (lowered lazily, cached, with its report part)       -> LineEval (W1-05a) -> floor curve
 *   every line's IR -> bezier registry (Phira scan order) -> EvalEnv (seconds + registry)
 *   every line's IR -> max_time contributions -> max_time -> every floor curve
 *   effects / videos -> extra.json IR
 *   line order -> father topology (LineScene pools), draw order, notes-seam topology
 *
 * Change -> recompile table (ChangeSet `DirtyScopes`):
 *
 *   | change                          | recompiled                                                                   |
 *   |---------------------------------|------------------------------------------------------------------------------|
 *   | lineMotion(line) / lineProps    | that line's IR and LineEval (anims, channels, controls, floor); its notes    |
 *   | lineNotes(line)                 | that line's IR (LineEval kept: its motion is unchanged), its max_time part,  |
 *   |                                 | its note runtime and multi-highlight contribution                            |
 *   | max_time changed                | the floor curve of every line (`setFloor`, O(speed keyframes)); the note     |
 *   |                                 | runtime (stored heights) only of lines whose notes sample the floor at or    |
 *   |                                 | after the first keyframe where old and new curve differ (`floorHorizon`:     |
 *   |                                 | both integrators re-segment at max_time, so heights in the segment that      |
 *   |                                 | contains the earlier max_time move too, F§7.2, F§7.3; heights before it are  |
 *   |                                 | bit-identical)                                                               |
 *   | tempo                           | tempo map, every LineEval (seconds), every max_time part, the IR of lines    |
 *   |                                 | whose speed lowering bakes in seconds; notes `onTempoChanged`; the IR of     |
 *   |                                 | every other line stays (beats)                                               |
 *   | timing / meta / rpeRoot /       | the chart-level IR only                                                      |
 *   | markers                         |                                                                              |
 *   | groups                          | the chart-level IR and every line's IR (Group index; LineEvals kept)         |
 *   | lineSet (order, add, delete)    | topology, draw order, bezier scan, LineEvals whose export index moved, the   |
 *   |                                 | IR of lines with a parent (father index), extra.json (attach index), notes   |
 *   |                                 | `dropLine` for deleted lines                                                 |
 *   | pkg                             | lowering options RE-DERIVED from doc.pkg: profile / speed mode / bake        |
 *   |                                 | tolerance -> every line's IR; speed mode / RPEVersion -> every floor curve   |
 *   |                                 | and note runtime; the chart-level IR and extra.json                          |
 *   | effects                         | extra.json IR                                                                |
 *   | assets                          | path -> asset map; IR and LineEval of the lines using a changed asset        |
 *   |                                 | (texture, GIF loop, size), IR of lines whose notes use it (hitsound paths);  |
 *   |                                 | chart-level IR; extra.json                                                   |
 *   | pkg / assets / meta with a      | additionally the IR of every line (the custom `LowerOptions.assetPath`       |
 *   | custom asset-path mapping       | may depend on them, e.g. RPE-id file naming)                                 |
 *   | bezier registry signature       | the LineEvals that evaluate bezier events (their floor curves come out       |
 *   |                                 | identical: speed keys never use a Bezier curve, model inv.keyBezierSpeed)    |
 *   | speed mode / RPEVersion         | every floor curve and every note runtime (heights)                           |
 *   | 'load' / 'replicaResync'        | everything, with fresh seam stores                                           |
 *
 * Note runtimes follow their line's floor curve. After a speed-settings change every line with notes is compiled
 * again. Whenever a line's floor curve is replaced otherwise (max_time, a recompiled LineEval), the graph compares
 * the old and the new curve (`floorHorizon`) and compiles the line's notes again only when one of them samples the
 * floor at or after the first difference (`LineNode.noteLatest`); compiled lines join the multi-highlight update.
 * A kept runtime is exact: its stored heights equal the new curve's at every sample time (by induction over syncs,
 * since every replacement of the curve is checked). The notes seam may still rebuild such a runtime lazily (its own
 * max_time freshness rule, CR-W1-05b-8), which never keeps stale heights.
 *
 * Per-track recompilation (A§7.1 "that track's IR events and anim") is coarser here: a key edit lowers its whole
 * line again and compiles its whole LineEval (W1-04 / W1-05a offer per-line entry points only; one line costs
 * about 1 ms). The floor curve is rebuilt with it, but a non-speed edit leaves the curve identical, which the
 * horizon check above sees; the line's notes are compiled again because the notes seam keys its runtime on the
 * IrLine object (CR-W1-05b-9).
 *
 * Evaluation is pull-based: a ChangeSet only records what is stale and bumps `revision`; the first query after it
 * (or `sync()`) recompiles. Purpose 'check' / 'export' also builds every note runtime and the emission table in
 * the same sync ("once per batch"); 'preview' leaves them to the notes seam's lazy, self-healing runtimes.
 *
 * The context handed to the seams (`EngineCompileContext`) is `GraphContext`: it reads the graph's current nodes.
 * Its `lineState` uses a pool separate from `Engine.lineStates` (change request CR-W1-06-4) and evaluates with the
 * aspect ratio and preview filters of the view being drawn (`withView`), else with the chart aspect. Its
 * `assetIdForPath` (contract, CR-W1-05b-3) maps package paths back to assets for custom hitsounds, shaders and videos.
 */

import type { EvalEnv } from './anim';
import {
  buildBezierRegistry,
  EXTENDED_BEZIER_SCAN,
  extendedOf,
  ignoredList,
  LAYER_BEZIER_SCAN,
  rawEventBezier,
  type BezierRegistry,
} from './bezierRegistry';
import { tempoOfChart } from './chartEval';
import { LineScene } from './compose';
import type { ControlWhich } from './controls';
import { floorHorizon } from './floorHorizon';
import { compileLineEval, type LineEval, type LineStateBuf, type SpeedSettings } from './lineState';
import { combineMaxTime, lineMaxTime } from './maxTime';
import { TransformCache, type LineTransform, type LineTransformSource } from './transformCache';
import type {
  EngineCompileContext,
  EngineEffectsModule,
  EngineNotesModule,
  EngineOptions,
  EngineSource,
  PreviewFilters,
} from './types';
import { chartAspect } from './viewport';
import { resolveRpe, ZERO_BEZIER } from '../ease/rpeMap';
import { LINEAR_TWEEN } from '../ease/tweens';
import type { Ease } from '../ease/types';
import { lowerChartIr } from '../ir/lower';
import { createLowerAcc, createLowerCtx, type LowerAcc, type LowerCtx } from '../ir/lowerCtx';
import { lowerExtraIr } from '../ir/lowerExtra';
import { lowerLineIr } from '../ir/lowerLine';
import type { IrChart, IrExtraJson, IrLine, LowerOptions, LowerReport } from '../ir/types';
import type { AssetId, ChartDoc, DocIndexes, KeyId, Line, LineId, PackageSettings } from '../model/types';
import type { ChangeSet } from '../ops/types';
import { createTempoMap } from '../time/tempo';
import type { Sec, TempoMap, Triple } from '../time/types';
import type { Unsubscribe } from '../util/types';

// ---------------------------------------------------------------------------------------------------------------
// Line nodes
// ---------------------------------------------------------------------------------------------------------------

/** Line dirt bits: lower the IR again, compile the LineEval again. */
export const DIRT_IR = 1;
export const DIRT_EVAL = 2;
export const DIRT_ALL = DIRT_IR | DIRT_EVAL;

/** What the frame needs to draw a line body besides its state (from the line record at lowering time). */
export interface LineDrawInfo {
  /** Texture asset (null = line.png). */
  readonly asset: AssetId | null;
  /** Texture pixel size from the asset metadata (null = unknown). */
  readonly texW: number | null;
  readonly texH: number | null;
  /** GIF loop duration in ms (null = unknown or not a GIF). */
  readonly gifTotalMs: number | null;
}

export interface LineNode {
  readonly id: LineId;
  /** Export index. */
  index: number;
  /** Lowered IR (null until first lowered). */
  ir: IrLine | null;
  /** This line's part of the lowering report. */
  acc: LowerAcc;
  /** Number of IR events of the line (stats). */
  irEvents: number;
  /** Compiled evaluation (null until first compiled). */
  le: LineEval | null;
  /** max_time contribution (seconds, -Infinity when none). */
  maxPart: number;
  /** Latest floor sample time of the line's notes (`latestNoteSample`). */
  noteLatest: number;
  /** The IR carries bezier events Phira's registry scan registers. */
  scans: boolean;
  draw: LineDrawInfo;
  /** Pending work (DIRT_* bits). */
  dirt: number;
}

const NO_DRAW_INFO: LineDrawInfo = { asset: null, texW: null, texH: null, gifTotalMs: null };

/** GIF loop duration of a line's texture asset in ms (null when unknown, not positive or no texture). */
export function gifTotalMsOf(doc: Pick<ChartDoc, 'assets'>, line: Pick<Line, 'texture'> | undefined): number | null {
  const tex = line?.texture ?? null;
  if (tex === null) return null;
  const meta = doc.assets.get(tex)?.meta;
  if (meta === undefined) return null;
  let total = meta.gifTotalMs;
  if (total === undefined && meta.gifDelaysMs !== undefined) {
    total = 0;
    for (const d of meta.gifDelaysMs) total += d;
  }
  return total !== undefined && Number.isFinite(total) && total > 0 ? total : null;
}

function positive(v: number | undefined): number | null {
  return v !== undefined && Number.isFinite(v) && v > 0 ? v : null;
}

/** Drawing data of a line's texture (asset id, pixel size, GIF loop). */
export function drawInfoOf(doc: Pick<ChartDoc, 'assets'>, line: Line | undefined): LineDrawInfo {
  if (line === undefined || line.texture === null) return NO_DRAW_INFO;
  const meta = doc.assets.get(line.texture)?.meta;
  return {
    asset: line.texture,
    texW: positive(meta?.width),
    texH: positive(meta?.height),
    gifTotalMs: gifTotalMsOf(doc, line),
  };
}

/** Number of events of every typed list of a lowered line. */
export function countIrEvents(ir: IrLine): number {
  let n = 0;
  for (const layer of ir.eventLayers) {
    if (layer === null) continue;
    n += layer.alphaEvents?.length ?? 0;
    n += layer.moveXEvents?.length ?? 0;
    n += layer.moveYEvents?.length ?? 0;
    n += layer.rotateEvents?.length ?? 0;
    n += layer.speedEvents?.length ?? 0;
  }
  const ext = extendedOf(ir);
  if (ext !== null) {
    n += ext.scaleXEvents?.length ?? 0;
    n += ext.scaleYEvents?.length ?? 0;
    n += ext.colorEvents?.length ?? 0;
    n += ext.textEvents?.length ?? 0;
    n += ext.inclineEvents?.length ?? 0;
    n += ext.paintEvents?.length ?? 0;
    n += ext.gifEvents?.length ?? 0;
  }
  return n;
}

/**
 * Latest time at which the notes seam samples the line's floor curve for its stored heights: every note's start
 * and end time (a hold samples its end; a non-hold's end is included as a safe upper bound), in seconds from the
 * same tempo walk the seam uses (`TempoMap.secAtTriple`). -Infinity without notes; +Infinity when a time is NaN (a
 * NaN sample reads the last keyframe, so it is never before a horizon).
 */
export function latestNoteSample(ir: Pick<IrLine, 'notes'>, sec: (t: Triple) => Sec): number {
  let m = Number.NEGATIVE_INFINITY;
  for (const n of ir.notes) {
    const a = sec(n.startTime);
    const b = sec(n.endTime);
    if (Number.isNaN(a) || Number.isNaN(b)) return Number.POSITIVE_INFINITY;
    if (a > m) m = a;
    if (b > m) m = b;
  }
  return m;
}

/** True when Phira's bezier registry scan registers something from this line (typed or written-back lists). */
export function scansBezier(ir: IrLine): boolean {
  const has = (list: ReadonlyArray<{ readonly bezier: number }> | undefined): boolean =>
    list !== undefined && list.some((e) => e.bezier !== 0);
  for (const layer of ir.eventLayers) {
    if (layer === null) continue;
    for (const key of LAYER_BEZIER_SCAN) if (has(layer[key])) return true;
  }
  const ext = extendedOf(ir);
  if (ext === null) return false;
  for (const key of EXTENDED_BEZIER_SCAN) {
    if (has(ext[key])) return true;
    const raw = ignoredList(ext, key);
    if (raw?.some((ev) => rawEventBezier(ev) !== null) === true) return true;
  }
  return false;
}

/** A tween segment Phira does not evaluate as plain linear (legacy speed mode bakes it). */
function easedTween(e: Ease): boolean {
  if (e.kind !== 'tween') return false;
  const desc = resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
  return !(desc.kind === 'static' && desc.tween === LINEAR_TWEEN);
}

/**
 * True when lowering the line's speed tracks reads the tempo map: the speed bake of a segment (a key with a
 * following key) eased by a bezier (any mode) or by a non-linear tween (legacy mode) works in seconds. A superset of
 * the baked segments is fine: those lines are lowered again after a tempo change.
 */
export function speedLoweringUsesTempo(doc: ChartDoc, idx: DocIndexes, line: LineId, legacy: boolean): boolean {
  const tracks = idx.tracksOfLine(line).get('speed');
  if (tracks === undefined) return false;
  for (const track of tracks) {
    const keys = idx.keysByTrack(track);
    for (let i = 0; i + 1 < keys.length; i++) {
      const e = doc.keys.get(keys[i] as KeyId)?.ease;
      if (e === undefined) continue;
      if (e.kind === 'bezier' || (legacy && easedTween(e))) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------------------------------------------
// Lowering
// ---------------------------------------------------------------------------------------------------------------

/** Lowering options re-derived from the document (`overrides` only for explicit "what if" previews). */
export function deriveLowerOptions(pkg: PackageSettings, lower: EngineOptions['lower']): LowerOptions {
  const o = lower?.overrides;
  const assetPath = o?.assetPath ?? lower?.assetPath;
  const base = {
    profile: o?.profile ?? pkg.exportProfile,
    speedMode: o?.speedMode ?? pkg.speedMode,
    speedBakeTolPx: o?.speedBakeTolPx ?? pkg.speedBakeTolPx,
  };
  return assetPath === undefined ? base : { ...base, assetPath };
}

/** doc.pkg with the lowering's speed mode applied (what the seams see as `ctx.pkg`; differs only under overrides). */
export function effectivePkgOf(pkg: PackageSettings, lower: Pick<LowerOptions, 'speedMode'>): PackageSettings {
  return lower.speedMode === pkg.speedMode ? pkg : { ...pkg, speedMode: lower.speedMode };
}

/** Speed settings of the floor integrators: the lowering's speed mode and the package's RPEVersion. */
export function speedSettingsOf(pkg: PackageSettings, lower: Pick<LowerOptions, 'speedMode'>): SpeedSettings {
  return { mode: lower.speedMode, rpeVersionEffective: pkg.rpeVersion };
}

/** Package path -> asset id map: the inverse of the lowering's asset paths (custom mapping, else the stored path). */
export function assetPathMap(
  assets: ChartDoc['assets'],
  custom: LowerOptions['assetPath'] | undefined,
): Map<string, AssetId> {
  const map = new Map<string, AssetId>();
  for (const [id, asset] of assets) {
    const p = custom === undefined ? asset.path : custom(id);
    if (!map.has(p)) map.set(p, id);
  }
  return map;
}

/** Looks a package path up in an `assetPathMap` (one leading '/' ignored). */
export function lookupAssetPath(map: ReadonlyMap<string, AssetId>, path: string): AssetId | null {
  const hit = map.get(path);
  if (hit !== undefined) return hit;
  return path.startsWith('/') ? (map.get(path.slice(1)) ?? null) : null;
}

export function sameLowering(a: LowerOptions, b: LowerOptions): boolean {
  return (
    a.profile === b.profile &&
    a.speedMode === b.speedMode &&
    Object.is(a.speedBakeTolPx, b.speedBakeTolPx) &&
    a.assetPath === b.assetPath
  );
}

/** A chart-level view of the document without lines (lowers META, BPMList, groups, time tags and root keys). */
export function headView(doc: ChartDoc): ChartDoc {
  return {
    kind: doc.kind,
    version: doc.version,
    idSeq: doc.idSeq,
    meta: doc.meta,
    pkg: doc.pkg,
    timing: doc.timing,
    editor: doc.editor,
    rpeRoot: doc.rpeRoot,
    bpm: doc.bpm,
    lines: doc.lines,
    lineOrder: [],
    tracks: doc.tracks,
    keys: doc.keys,
    notes: doc.notes,
    effects: doc.effects,
    effectOrder: doc.effectOrder,
    videos: doc.videos,
    markers: doc.markers,
    groups: doc.groups,
    generators: doc.generators,
    assets: doc.assets,
  };
}

/**
 * Lowering contexts of one sync: one W1-04 context (`createLowerCtx`, its lazily built tempo map and group layout
 * shared by every part) with one report accumulator per part. Only `acc` is replaced, so members W1-04 adds to
 * `LowerCtx` carry over unchanged (CR-W1-05b-6); its methods keep their state in closures, never in `acc`.
 */
function lowerCtxFactory(doc: ChartDoc, idx: DocIndexes, opts: LowerOptions): (acc: LowerAcc) => LowerCtx {
  const shared = createLowerCtx(doc, idx, opts);
  return (acc) => ({ ...shared, acc });
}

/** Sum of lowering report parts, in `lowerChart`'s order (chart head, lines in export order, extra.json). */
export function sumReports(parts: readonly LowerAcc[]): LowerReport {
  const bakedSpeed: LowerReport['bakedSpeed'][number][] = [];
  const paddedLines: LineId[] = [];
  let stale = 0;
  let gaps = 0;
  for (const p of parts) {
    bakedSpeed.push(...p.bakedSpeed);
    paddedLines.push(...p.paddedLines);
    stale += p.staleRawTriples;
    gaps += p.gapsMaterialised;
  }
  return { bakedSpeed, paddedLines, staleRawTriples: stale, gapsMaterialised: gaps };
}

// ---------------------------------------------------------------------------------------------------------------
// Pending work
// ---------------------------------------------------------------------------------------------------------------

interface Pending {
  full: boolean;
  pkg: boolean;
  tempo: boolean;
  head: boolean;
  extra: boolean;
  order: boolean;
  groups: boolean;
  /** META changed (custom asset paths may depend on it, e.g. RPE-id file naming). */
  meta: boolean;
  assets: boolean;
  /** Changed asset ids (null = unknown: every line is treated as a user). */
  assetIds: Set<AssetId> | null;
  /** Line -> DIRT_* bits from ChangeSets. */
  readonly lines: Map<LineId, number>;
}

function emptyPending(full = false): Pending {
  return {
    full,
    pkg: false,
    tempo: false,
    head: false,
    extra: false,
    order: false,
    groups: false,
    meta: false,
    assets: false,
    assetIds: new Set(),
    lines: new Map(),
  };
}

/** The seams the graph and the engine delegate to (W1-06 notes, W1-08 effects). */
export interface EngineSeams {
  readonly notes: EngineNotesModule;
  readonly effects: EngineEffectsModule;
}

/** What one sync did (diagnostics and tests). */
export interface SyncReport {
  readonly full: boolean;
  /** Lines whose IR was lowered again, in export order. */
  readonly lowered: readonly LineId[];
  /** Lines whose LineEval was compiled again, in export order. */
  readonly compiled: readonly LineId[];
  readonly removed: readonly LineId[];
  readonly orderChanged: boolean;
  readonly tempoChanged: boolean;
  readonly maxTimeChanged: boolean;
  readonly floorsRebuilt: boolean;
  /** Lines whose note runtime was compiled again (`compileLineNotes`), in export order. */
  readonly notesCompiled: readonly LineId[];
  /**
   * Lines with notes whose floor curve was replaced but whose note runtime was kept (every note samples the floor
   * before the first difference of old and new curve), in export order.
   */
  readonly notesKept: readonly LineId[];
  readonly registryChanged: boolean;
  readonly headLowered: boolean;
  readonly extraLowered: boolean;
}

export function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function secOf(tempo: TempoMap): EvalEnv['sec'] {
  return (t) => tempo.secAtTriple(t);
}

function sameOrder(a: readonly LineId[], b: readonly LineId[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// The graph
// ---------------------------------------------------------------------------------------------------------------

export class EngineGraph {
  readonly src: EngineSource;
  readonly opts: EngineOptions;
  readonly seams: EngineSeams;
  readonly ctx: GraphContext;
  /** 'check' / 'export': build every note runtime and the emission table in each sync. */
  readonly eager: boolean;

  /** Bumped by every ChangeSet that may change compiled output. */
  revision = 0;
  /** Syncs that did work. */
  syncCount = 0;
  lastSyncMs = 0;
  lastSync: SyncReport | null = null;

  lowerOpts: LowerOptions;
  /** doc.pkg with the speed-mode override applied (what the seams see as `ctx.pkg`). */
  pkg: PackageSettings;
  speed: SpeedSettings;
  tempo: TempoMap;
  env: EvalEnv;
  registry: BezierRegistry;
  maxTime: Sec = 1;
  /** Chart-level IR (empty judgeLineList) and its report part. */
  head: IrChart | null = null;
  headAcc: LowerAcc = createLowerAcc();
  extra: IrExtraJson | null = null;
  extraAcc: LowerAcc = createLowerAcc();
  /** Line ids in export order (a new array whenever the order or the line set changes). */
  order: readonly LineId[] = [];
  readonly nodes = new Map<LineId, LineNode>();
  /** LineEvals in export order. */
  evals: readonly LineEval[] = [];
  /** Pool of Engine.lineStates / lineState / frame. */
  readonly main: LineScene = new LineScene([]);
  /** Pool of EngineCompileContext.lineState (separate from `main`, CR-W1-06-4). */
  readonly aux: LineScene = new LineScene([]);
  /** Transforms at fixed times (GraphContext.lineTransform), valid for one syncCount. */
  private readonly transforms = new TransformCache();
  notesStore = new Map<LineId, unknown>();
  effectsStore = new Map<string, unknown>();

  /** Aspect ratio and filters `ctx.lineState` evaluates with (set while a view is drawn). */
  viewAr: number | null = null;
  viewFilters: PreviewFilters | undefined = undefined;
  /** Aspect ratio of the last frame drawn (paint stamps follow the drawn frame). */
  lastFrameAr: number | null = null;

  private pending: Pending = emptyPending(true);
  private dirty = true;
  private syncing = false;
  private disposed = false;
  private unsubscribe: Unsubscribe | null;
  private chartCache: IrChart | null = null;
  private reportCache: LowerReport | null = null;
  private drawOrderCache: Int32Array | null = null;
  private assetByPath: Map<string, AssetId> | null = null;
  private onSynced: (() => void) | null = null;

  constructor(src: EngineSource, opts: EngineOptions, seams: EngineSeams) {
    this.src = src;
    this.opts = opts;
    this.seams = seams;
    this.eager = opts.purpose !== 'preview';
    this.ctx = new GraphContext(this);
    const pkg = src.doc.pkg;
    this.lowerOpts = deriveLowerOptions(pkg, opts.lower);
    this.pkg = this.effectivePkg(pkg);
    this.speed = speedSettingsOf(pkg, this.lowerOpts);
    this.tempo = createTempoMap([{ bpm: 120, startTime: [0, 0, 1] }]);
    this.registry = buildBezierRegistry([]);
    this.env = { sec: secOf(this.tempo), bezier: this.registry };
    this.unsubscribe = src.subscribe((cs) => {
      this.onChange(cs);
    });
  }

  /** Called after every sync that did work (the engine's verify mode). */
  setSyncListener(fn: (() => void) | null): void {
    this.onSynced = fn;
  }

  /** Something is waiting for the next sync. */
  get isDirty(): boolean {
    return this.dirty;
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.nodes.clear();
    this.evals = [];
    this.order = [];
    this.notesStore = new Map();
    this.effectsStore = new Map();
    this.main.setLines([]);
    this.aux.setLines([]);
  }

  // ------------------------------------------------------------------------------------------------------------
  // Change intake
  // ------------------------------------------------------------------------------------------------------------

  /** Records what a ChangeSet made stale and bumps the revision (no recompilation here). */
  onChange(cs: ChangeSet): void {
    if (this.disposed) return;
    const p = this.pending;
    if (cs.source === 'load' || cs.source === 'replicaResync') {
      p.full = true;
      this.touch();
      return;
    }
    const d = cs.dirty;
    let relevant = false;
    const mark = (id: LineId, bits: number): void => {
      p.lines.set(id, (p.lines.get(id) ?? 0) | bits);
      relevant = true;
    };
    if (d.pkg) {
      p.pkg = true;
      relevant = true;
    }
    if (d.tempo) {
      p.tempo = true;
      relevant = true;
    }
    if (d.timing || d.meta || d.rpeRoot || d.markers) {
      p.head = true;
      p.meta ||= d.meta;
      relevant = true;
    }
    if (d.groups) {
      p.head = true;
      p.groups = true;
      relevant = true;
    }
    if (d.lineSet) {
      p.order = true;
      relevant = true;
    }
    for (const id of d.lineProps) mark(id, DIRT_ALL);
    for (const id of d.lineMotion.keys()) mark(id, DIRT_ALL);
    for (const id of d.lineNotes) mark(id, DIRT_IR);
    if (d.effects) {
      p.extra = true;
      relevant = true;
    }
    if (d.assets) {
      p.assets = true;
      p.head = true;
      p.extra = true;
      relevant = true;
      const ids = p.assetIds;
      if (ids !== null) {
        if (cs.ops.length === 0) p.assetIds = null;
        for (const op of cs.ops) {
          if ((op.o === 'ins' || op.o === 'del') && op.tbl === 'assets') ids.add(op.rec.id);
          else if (op.o === 'set' && op.tbl === 'assets') ids.add(op.id);
        }
      }
    }
    if (relevant) this.touch();
  }

  /** Marks everything stale (fresh seam stores at the next sync). */
  invalidateAll(): void {
    this.pending.full = true;
    this.touch();
  }

  private touch(): void {
    this.dirty = true;
    this.revision++;
  }

  // ------------------------------------------------------------------------------------------------------------
  // Sync
  // ------------------------------------------------------------------------------------------------------------

  /** Recompiles whatever is stale (no-op when clean, disposed, or while a sync is running). */
  sync(): void {
    if (!this.dirty || this.syncing || this.disposed) return;
    this.syncing = true;
    const t0 = nowMs();
    const p = this.pending;
    this.pending = emptyPending();
    this.dirty = false;
    // Compiled lines may change in place (floors): no pooled states survive a sync.
    this.main.invalidate();
    this.aux.invalidate();
    try {
      this.lastSync = this.recompile(p);
    } catch (err) {
      // Start from scratch next time: a half-applied recompilation is never reused.
      this.pending = emptyPending(true);
      this.dirty = true;
      throw err;
    } finally {
      this.syncing = false;
    }
    this.lastSyncMs = nowMs() - t0;
    this.syncCount++;
    this.onSynced?.();
  }

  private effectivePkg(pkg: PackageSettings): PackageSettings {
    return effectivePkgOf(pkg, this.lowerOpts);
  }

  private resetAll(): void {
    this.nodes.clear();
    this.evals = [];
    this.order = [];
    this.head = null;
    this.extra = null;
    this.assetByPath = null;
    this.notesStore = new Map();
    this.effectsStore = new Map();
    this.main.setLines([]);
    this.aux.setLines([]);
  }

  private newNode(id: LineId, index: number): LineNode {
    return {
      id,
      index,
      ir: null,
      acc: createLowerAcc(),
      irEvents: 0,
      le: null,
      maxPart: Number.NEGATIVE_INFINITY,
      noteLatest: Number.NEGATIVE_INFINITY,
      scans: false,
      draw: NO_DRAW_INFO,
      dirt: DIRT_ALL,
    };
  }

  private recompile(p: Pending): SyncReport {
    const doc = this.src.doc;
    const idx = this.src.idx;
    const notes = this.seams.notes;
    const full = p.full;
    if (full) this.resetAll();

    // 1. Package settings -> lowering options (re-derived from doc.pkg), speed settings, effective pkg.
    let floorsStale = full;
    let speedChanged = full;
    let head = p.head || full;
    let extra = p.extra || full;
    if (full || p.pkg) {
      const pkg = doc.pkg;
      const lo = deriveLowerOptions(pkg, this.opts.lower);
      if (!sameLowering(lo, this.lowerOpts)) {
        for (const node of this.nodes.values()) node.dirt |= DIRT_ALL;
        this.assetByPath = null;
      }
      this.lowerOpts = lo;
      this.pkg = this.effectivePkg(pkg);
      const speed = speedSettingsOf(pkg, lo);
      if (speed.mode !== this.speed.mode || speed.rpeVersionEffective !== this.speed.rpeVersionEffective) {
        floorsStale = true;
        speedChanged = true;
      }
      this.speed = speed;
      head = true;
      extra = true;
    }

    // 2. Line order and line set.
    const removed: LineId[] = [];
    let orderChanged = false;
    if (full || p.order) {
      const next = doc.lineOrder;
      if (full || !sameOrder(next, this.order)) {
        orderChanged = true;
        const live = new Set(next);
        for (const id of this.order) if (!live.has(id)) removed.push(id);
        for (const id of removed) this.nodes.delete(id);
        const newOrder = [...next];
        newOrder.forEach((id, i) => {
          const node = this.nodes.get(id);
          if (node === undefined) {
            this.nodes.set(id, this.newNode(id, i));
            return;
          }
          if (node.index !== i) node.dirt |= DIRT_EVAL;
          node.index = i;
          // The father index of a line with a parent may have moved.
          const parent = doc.lines.get(id)?.parent ?? null;
          if (parent !== null) node.dirt |= DIRT_ALL;
        });
        this.order = newOrder;
        extra = true;
      }
    }

    // 3. Line dirt from ChangeSets, groups and assets.
    for (const [id, bits] of p.lines) {
      const node = this.nodes.get(id);
      if (node !== undefined) node.dirt |= bits;
    }
    if (p.groups) for (const node of this.nodes.values()) node.dirt |= DIRT_IR;
    if (p.assets) {
      this.assetByPath = null;
      this.markAssetUsers(p.assetIds);
    }
    // A custom asset-path mapping (export file naming) may depend on the package settings, the asset table or META:
    // every path it produced is re-derived.
    if (this.lowerOpts.assetPath !== undefined && !full && (p.pkg || p.assets || p.meta)) {
      this.assetByPath = null;
      for (const node of this.nodes.values()) node.dirt |= DIRT_IR;
      extra = true;
    }

    // 4. Chart-level IR and tempo.
    const factory = lowerCtxFactory(doc, idx, this.lowerOpts);
    let headIr = this.head;
    let headLowered = false;
    if (headIr === null || head || p.tempo) {
      const acc = createLowerAcc();
      headIr = lowerChartIr({ ...factory(acc), doc: headView(doc) });
      this.head = headIr;
      this.headAcc = acc;
      headLowered = true;
    }
    const tempoChanged = full || p.tempo;
    if (tempoChanged) {
      this.tempo = tempoOfChart(headIr);
      this.env = { sec: secOf(this.tempo), bezier: this.registry };
      extra = true;
      const legacy = this.lowerOpts.speedMode === 'legacy';
      for (const node of this.nodes.values()) {
        node.dirt |= DIRT_EVAL;
        if (speedLoweringUsesTempo(doc, idx, node.id, legacy)) node.dirt |= DIRT_IR;
      }
    }

    // 5. Lower stale lines (max_time parts follow the IR, or the tempo).
    const lowered: LineId[] = [];
    let registryCheck = orderChanged;
    for (const id of this.order) {
      const node = this.nodes.get(id);
      if (node === undefined) continue;
      if ((node.dirt & DIRT_IR) === 0 && node.ir !== null) {
        if (tempoChanged) {
          node.maxPart = lineMaxTime(node.ir, this.env.sec);
          node.noteLatest = latestNoteSample(node.ir, this.env.sec);
        }
        continue;
      }
      const acc = createLowerAcc();
      const ir = lowerLineIr(factory(acc), id);
      const scans = scansBezier(ir);
      // Only motion changes (DIRT_EVAL) can change the scanned lists; notes / Group / path-only changes cannot.
      if ((node.dirt & DIRT_EVAL) !== 0 && (node.scans || scans)) registryCheck = true;
      if (node.le === null) node.dirt |= DIRT_EVAL;
      node.ir = ir;
      node.acc = acc;
      node.irEvents = countIrEvents(ir);
      node.scans = scans;
      node.draw = drawInfoOf(doc, doc.lines.get(id));
      node.maxPart = lineMaxTime(ir, this.env.sec);
      node.noteLatest = latestNoteSample(ir, this.env.sec);
      lowered.push(id);
    }

    // 6. Bezier registry (Phira scan order over every line); bezier users recompile when it changed. Their floor
    // curves come out identical (speed keys never use a Bezier curve), so their note runtimes are kept.
    let registryChanged = false;
    if (full || registryCheck) {
      const reg = buildBezierRegistry(this.order.map((id) => this.irOfNode(id)));
      if (full || reg.signature !== this.registry.signature) {
        registryChanged = !full;
        this.registry = reg;
        this.env = { sec: this.env.sec, bezier: reg };
        for (const node of this.nodes.values()) if (node.le?.usesBezier === true) node.dirt |= DIRT_EVAL;
      }
    }

    // 7. max_time.
    const maxTime = combineMaxTime(this.order.map((id) => this.nodes.get(id)?.maxPart ?? Number.NEGATIVE_INFINITY));
    const maxTimeChanged = !Object.is(maxTime, this.maxTime);
    if (maxTimeChanged) floorsStale = true;
    this.maxTime = maxTime;

    // 8. LineEvals and floor curves. Unless every note runtime is rebuilt anyway (tempo, speed settings, full),
    // every replaced floor curve is compared with its predecessor: `horizons` maps the line to the time before
    // which both give identical heights.
    const allNotes = full || tempoChanged || speedChanged;
    const horizons = new Map<LineId, number>();
    const compiled: LineId[] = [];
    const evals: LineEval[] = [];
    for (const id of this.order) {
      const node = this.nodes.get(id);
      if (node === undefined) continue;
      let le = node.le;
      if (le === null || (node.dirt & DIRT_EVAL) !== 0) {
        const prev = le?.floor ?? null;
        le = compileLineEval(this.irOfNode(id), this.env, {
          index: node.index,
          line: id,
          gifTotalMs: node.draw.gifTotalMs,
        });
        le.setFloor(maxTime, this.speed);
        if (!allNotes && prev !== null) horizons.set(id, floorHorizon(prev, le.floor));
        node.le = le;
        compiled.push(id);
        if (!orderChanged) {
          this.main.setLine(node.index, le);
          this.aux.setLine(node.index, le);
        }
      } else if (floorsStale) {
        const prev = le.floor;
        le.setFloor(maxTime, this.speed);
        if (!allNotes) horizons.set(id, floorHorizon(prev, le.floor));
      }
      node.dirt = 0;
      evals.push(le);
    }
    this.evals = evals;
    if (orderChanged) {
      this.main.setLines(evals);
      this.aux.setLines(evals);
    }
    if (orderChanged || lowered.length > 0) this.drawOrderCache = null;

    // 9. extra.json.
    let extraLowered = false;
    if (extra || this.extra === null) {
      const acc = createLowerAcc();
      this.extra = lowerExtraIr(factory(acc), headIr.BPMList);
      this.extraAcc = acc;
      extraLowered = true;
    }
    if (headLowered || extraLowered || lowered.length > 0 || orderChanged) {
      this.chartCache = null;
      this.reportCache = null;
    }

    // 10. Notes seam: deleted lines, tempo, changed runtimes, multi-highlight, emissions. After a tempo change every
    // note second is stale: every line is rebuilt so that the global multi-highlight map is exact again (per-note
    // queries of the seam only refresh the note's own line). Runtimes store heights from the floor curve: after a
    // speed-settings change every line with notes is rebuilt, after other floor replacements (max_time) only the
    // lines with a note at or after the floor horizon.
    for (const id of removed) notes.dropLine(this.ctx, id);
    if (tempoChanged && !full) notes.onTempoChanged(this.ctx);
    const notesKept: LineId[] = [];
    const noteLines = tempoChanged ? this.order : this.noteLinesOf(lowered, allNotes, horizons, notesKept);
    for (const id of noteLines) notes.compileLineNotes(this.ctx, id);
    const changedNotes = new Set<LineId>([...noteLines, ...removed]);
    if (changedNotes.size > 0) notes.updateMultiHint(this.ctx, changedNotes);
    if (this.eager || p.assets) notes.rebuildEmissions(this.ctx);

    return {
      full,
      lowered,
      compiled,
      removed,
      orderChanged,
      tempoChanged,
      maxTimeChanged,
      floorsRebuilt: floorsStale,
      notesCompiled: noteLines,
      notesKept,
      registryChanged,
      headLowered,
      extraLowered,
    };
  }

  /**
   * Lines whose note runtime must be compiled again, in export order: the lowered lines (a new IrLine) and every
   * other line with notes when `all` (speed settings changed), else those whose floor curve was replaced
   * (`horizons`) and that have a note sampling the floor at or after the horizon. Lines with notes whose curve was
   * replaced but whose runtime stays exact are appended to `kept`.
   */
  private noteLinesOf(
    lowered: readonly LineId[],
    all: boolean,
    horizons: ReadonlyMap<LineId, number>,
    kept: LineId[],
  ): readonly LineId[] {
    if (!all && horizons.size === 0) return lowered;
    const relowered = new Set(lowered);
    const out: LineId[] = [];
    for (const id of this.order) {
      if (relowered.has(id)) {
        out.push(id);
        continue;
      }
      const node = this.nodes.get(id);
      const ir = node?.ir ?? null;
      if (node === undefined || ir === null || ir.notes.length === 0) continue;
      if (all) {
        out.push(id);
        continue;
      }
      const horizon = horizons.get(id);
      if (horizon === undefined) continue;
      if (node.noteLatest < horizon) kept.push(id);
      else out.push(id);
    }
    return out;
  }

  private markAssetUsers(ids: ReadonlySet<AssetId> | null): void {
    if (ids === null) {
      for (const node of this.nodes.values()) node.dirt |= DIRT_ALL;
      return;
    }
    const doc = this.src.doc;
    const idx = this.src.idx;
    for (const asset of ids) {
      const users = idx.assetUsers(asset);
      for (const line of users.lines) {
        const node = this.nodes.get(line);
        if (node !== undefined) node.dirt |= DIRT_ALL;
      }
      for (const note of users.notes) {
        const line = doc.notes.get(note)?.line;
        const node = line === undefined ? undefined : this.nodes.get(line);
        if (node !== undefined) node.dirt |= DIRT_IR;
      }
    }
  }

  // ------------------------------------------------------------------------------------------------------------
  // Read access (callers sync first)
  // ------------------------------------------------------------------------------------------------------------

  private irOfNode(id: LineId): IrLine {
    const ir = this.nodes.get(id)?.ir ?? null;
    if (ir === null) throw new RangeError(`engine: line ${String(id)} is not in the chart`);
    return ir;
  }

  /** The node of a line of the current order, or undefined. */
  node(line: LineId): LineNode | undefined {
    return this.nodes.get(line);
  }

  /** Lowered IR of a line (throws for an unknown line). */
  irLine(line: LineId): IrLine {
    return this.irOfNode(line);
  }

  /** Compiled evaluation of a line (throws for an unknown line). */
  lineEval(line: LineId): LineEval {
    const le = this.nodes.get(line)?.le ?? null;
    if (le === null) throw new RangeError(`engine: line ${String(line)} is not in the chart`);
    return le;
  }

  /** Compiled evaluation of a line, or null for an unknown line. */
  lineEvalOrNull(line: LineId): LineEval | null {
    return this.nodes.get(line)?.le ?? null;
  }

  /** The full chart IR (lines in export order), assembled from the cached parts (cached until a change). */
  chart(): IrChart {
    if (this.chartCache === null) {
      const head = this.head;
      if (head === null) throw new RangeError('engine: the chart is not compiled');
      this.chartCache = { ...head, judgeLineList: this.order.map((id) => this.irOfNode(id)) };
    }
    return this.chartCache;
  }

  /** The lowering report of the whole chart (sum of the cached parts in lowerChart's order). */
  report(): LowerReport {
    if (this.reportCache === null) {
      const parts: LowerAcc[] = [this.headAcc];
      for (const id of this.order) {
        const node = this.nodes.get(id);
        if (node !== undefined) parts.push(node.acc);
      }
      parts.push(this.extraAcc);
      this.reportCache = sumReports(parts);
    }
    return this.reportCache;
  }

  /** Export indices of the drawn line bodies in Phira's (zOrder, index) order; attachUI lines excluded (R§3.10). */
  drawOrder(): Int32Array {
    if (this.drawOrderCache === null) {
      this.drawOrderCache = drawOrderOf(this.order.map((id) => this.irOfNode(id)));
    }
    return this.drawOrderCache;
  }

  /** Drawing data (texture asset, pixel size, GIF loop) of the line at an export index. */
  drawInfoAt(index: number): LineDrawInfo {
    const id = this.order[index];
    return (id === undefined ? undefined : this.nodes.get(id)?.draw) ?? NO_DRAW_INFO;
  }

  /** Chart aspect (pkg aspectRatio) used by queries that have no viewport. */
  chartAr(): number {
    return chartAspect(this.src.doc.pkg);
  }

  /** Package path (one leading '/' ignored) -> asset id: the inverse of the lowering's asset paths. */
  assetIdForPath(path: string): AssetId | null {
    this.assetByPath ??= assetPathMap(this.src.doc.assets, this.lowerOpts.assetPath);
    return lookupAssetPath(this.assetByPath, path);
  }

  /**
   * Transform of a line at t with the current view's aspect and filters, cached across frames while the compiled
   * graph is unchanged (hit-effect placement at emission times; transformCache.ts).
   */
  lineTransform(line: LineId, t: Sec): LineTransform {
    const node = this.nodes.get(line);
    if (node === undefined) throw new RangeError(`engine: line ${String(line)} is not in the chart`);
    const ar = this.viewAr ?? this.chartAr();
    const filters = this.viewFilters;
    return this.transforms.get(node.index, t, this.syncCount, ar, filters?.mutedLayers, () =>
      this.aux.evalLine(node.index, t, ar, filters),
    );
  }

  /** State of a line in the context pool, with the current view's aspect and filters. */
  auxState(line: LineId, t: Sec): LineStateBuf {
    const node = this.nodes.get(line);
    if (node === undefined) throw new RangeError(`engine: line ${String(line)} is not in the chart`);
    return this.aux.evalLine(node.index, t, this.viewAr ?? this.chartAr(), this.viewFilters);
  }

  /** Runs `fn` with `ctx.lineState` evaluating at aspect `ar` under `filters` (restored afterwards). */
  withView<R>(ar: number, filters: PreviewFilters | undefined, fn: () => R): R {
    const prevAr = this.viewAr;
    const prevFilters = this.viewFilters;
    this.viewAr = ar;
    this.viewFilters = filters;
    try {
      return fn();
    } finally {
      this.viewAr = prevAr;
      this.viewFilters = prevFilters;
    }
  }
}

/** Export indices of the drawn line bodies of lowered lines, in (zOrder, index) order, attachUI excluded. */
export function drawOrderOf(lines: readonly Pick<IrLine, 'zOrder' | 'attachUI'>[]): Int32Array {
  const items: { index: number; z: number }[] = [];
  lines.forEach((ir, index) => {
    if (ir.attachUI !== null) return;
    items.push({ index, z: ir.zOrder });
  });
  items.sort((a, b) => a.z - b.z || a.index - b.index);
  return Int32Array.from(items, (it) => it.index);
}

// ---------------------------------------------------------------------------------------------------------------
// Compile context
// ---------------------------------------------------------------------------------------------------------------

/**
 * `EngineCompileContext` over the graph's current nodes (valid after a sync: the engine syncs before every seam
 * call).
 */
export class GraphContext implements EngineCompileContext, LineTransformSource {
  private readonly g: EngineGraph;

  constructor(g: EngineGraph) {
    this.g = g;
  }

  get tempo(): TempoMap {
    return this.g.tempo;
  }

  get maxTime(): Sec {
    return this.g.maxTime;
  }

  get pkg(): PackageSettings {
    return this.g.pkg;
  }

  get meta(): { readonly name: string; readonly level: string } {
    return this.g.src.doc.meta;
  }

  get notesStore(): Map<LineId, unknown> {
    return this.g.notesStore;
  }

  get effectsStore(): Map<string, unknown> {
    return this.g.effectsStore;
  }

  irLine(line: LineId): IrLine {
    return this.g.irLine(line);
  }

  irExtra(): IrExtraJson | null {
    return this.g.extra;
  }

  lineOrder(): readonly LineId[] {
    return this.g.order;
  }

  floorAt(line: LineId, t: Sec): number {
    return this.g.lineEval(line).floor.heightAt(t);
  }

  lineState(line: LineId, t: Sec): LineStateBuf {
    return this.g.auxState(line, t);
  }

  /** Cached transform at t (LineTransformSource, beyond the contract). */
  lineTransform(line: LineId, t: Sec): LineTransform {
    return this.g.lineTransform(line, t);
  }

  control(line: LineId, which: ControlWhich, h: number): number | null {
    return this.g.lineEval(line).controls.at(which, h);
  }

  /** Package path -> asset id (custom hitsounds, shaders, videos). */
  assetIdForPath(path: string): AssetId | null {
    return this.g.assetIdForPath(path);
  }
}
